/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { createSlice } from '@reduxjs/toolkit';
import { signInActions } from 'modules/sign-in/slice';
import { ALIGNED, REPORT_TYPE, SLICE, TAGS } from 'modules/dashboard/constants';
import { dashboardActions } from 'modules/dashboard/slice';
import { capitalizeFirst } from 'modules/common/helpers/strings';
import { isEmpty } from 'modules/common/helpers/object';
import { getHotelFocusOnValue } from 'modules/dashboard/functions';
//
const stayDate = {
  startDate: new Date().toISOString(),
  endDate: new Date().toISOString(),
  key: 'selection',
};
/**
 * Initial states of Workbook widget functions are defined here
 */
export const initialState = {
  loading: false,
  workbookBookingDate: null,
  dateComparison: null,
  workbookFilterList: [],
  workbookHotelFilterList: [],
  workbookAlignmentToggle: null,
  workbookOtb: false,
  workbookCustomDate: 'start',
  workbookStartDate: stayDate.startDate,
  workbookEndDate: stayDate.endDate,
  workbookDateSelected: stayDate.startDate,
  workbookVisualToggle: false,
  workbookUrl: '',
  exportUrl: '',
  symmetricComparisonToggle: true,
  alignment: null,
  exportData: null,
  symmetricComparisonIsDisplay: false,
  d1SetIsDisplay: true,
  d2SetIsDisplay: true,
  d3SetIsDisplay: true,
  enableSecondaryHotelGroups: false,
  hotelFocusOn: {},
  normalizedView: false,
  workbookHotelVisuals: [],
  isWorkbookHotelComparison: false,
  isRatePlan: false,
  isRoomNightsPace: false,
  isRoomType: false,
  ratePlanVisuals: [],
  ratePlanFilterList: [],
  originalFocusOn: null,
  //
  isTravelAgent: false,
  travelAgentDetailsOnVisuals: [],
  travelAgentDetailsOffVisuals: [],
  isDetailed: false,
  isSecondaryHotelSetOverTime: false,
  isSecondaryHotelSetOverTimeDisabled: false,
  invalidSecondaryHotelList: [],
  ratePlanData: null,
  paceData: null,
  iataData: null,
  roomTypeData: null,
  consortiaData: null,
  isLeapSelection: false,
};
/**
 * All actions related to summary widget feature are defined here
 */
export const workbookSlice = createSlice({
  name: SLICE.WORKBOOK_REPORT,
  initialState,
  reducers: {
    setWorkbookBookingDate(state, action) {
      state.workbookBookingDate = action?.payload;
    },
    setBreakdown(state, action) {
      state.chartBreakdown = action?.payload;
    },
    setDateComparison(state, action) {
      state.dateComparison = action?.payload;
    },
    updateWorkbookFilterList(state, action) {
      state.workbookFilterList = action?.payload;
    },
    updateWorkbookHotelFilterList(state, action) {
      state.workbookHotelFilterList = action?.payload;
    },
    updateRatePlanFilterList(state, action) {
      state.ratePlanFilterList = action?.payload;
    },
    setWorkbookAlignmentToggle(state, action) {
      state.workbookAlignmentToggle = action?.payload;
    },
    setWorkbookOtb(state, action) {
      state.workbookOtb = action?.payload;
    },
    setWorkbookCustomDate(state, action) {
      state.workbookCustomDate = action?.payload;
    },
    setWorkbookStartDate(state, action) {
      state.workbookStartDate = action?.payload;
    },
    setWorkbookEndDate(state, action) {
      state.workbookEndDate = action?.payload;
    },
    setWorkbookDateSelected(state, action) {
      state.workbookDateSelected = action?.payload;
    },
    setWorkbookVisualToggle(state, action) {
      state.workbookVisualToggle = action.payload;
    },
    setSymmetricComparisonToggle(state, action) {
      state.symmetricComparisonToggle = action?.payload;
    },
    setIsDetailed(state, action) {
      state.isDetailed = action?.payload;
    },
    setPaceVisualFilterList(state, action) {
      state.paceVisualFilterList = action?.payload;
    },
    setAlignment(state, action) {
      state.alignment = action?.payload;
    },
    getWorkbookPdfExportUrl(state) {
      state.loading = true;
    },
    getWorkbookPdfExportUrlSucceeded(state, action) {
      state.exportData = action?.payload;
      state.loading = false;
    },
    getWorkbookPdfExportUrlFailed(state) {
      state.loading = false;
    },
    setWorkbookUrl(state, action) {
      state.workbookUrl = action?.payload;
    },
    setExportUrl(state, action) {
      state.exportUrl = action?.payload;
    },
    setPdfContent(state, action) {
      state.pdf = action.payload;
      state.workbookUrl = action?.payload;
    },
    setSymmetricComparisonIsDisplay(state, action) {
      state.symmetricComparisonIsDisplay = action?.payload;
    },
    setD1SetIsDisplay(state, action) {
      state.d1SetIsDisplay = action?.payload;
    },
    setD2SetIsDisplay(state, action) {
      state.d2SetIsDisplay = action?.payload;
    },
    setD3SetIsDisplay(state, action) {
      state.d3SetIsDisplay = action?.payload;
    },
    setEnableSecondaryHotelGroups(state, action) {
      state.enableSecondaryHotelGroups = action.payload;
    },
    setHotelFocusOn(state, action) {
      state.hotelFocusOn = action?.payload;
      state.enableSecondaryHotelGroups = action?.payload && 'hotels' in action.payload;
    },
    setNormalizedView(state, action) {
      state.normalizedView = action?.payload;
    },
    setIsWorkbookHotelComparison(state, action) {
      state.isWorkbookHotelComparison = action?.payload;
    },
    setIsRatePlan(state, action) {
      state.isRatePlan = action?.payload;
    },
    setIsRoomNightsPace(state, action) {
      state.isRoomNightsPace = action?.payload;
    },
    setIsRoomType(state, action) {
      state.isRoomType = action?.payload;
    },
    setOriginalHotelFocusOn: (state, action) => {
      state.originalFocusOn = action.payload;
    },
    resetHotelFocusOn: (state, action) => {
      if (state.originalFocusOn !== null) {
        state.hotelFocusOn = action?.payload;
        state.enableSecondaryHotelGroups = action?.payload && 'hotels' in action.payload;
      }
    },
    setIsTravelAgent(state, action) {
      state.isTravelAgent = action?.payload;
    },
    setIsSecondaryHotelSetOverTime(state, action) {
      state.isSecondaryHotelSetOverTime = action?.payload;
    },
    setIsSecondaryHotelSetOverTimeDisabled(state, action) {
      state.isSecondaryHotelSetOverTimeDisabled = action?.payload;
    },
    setInvalidSecondaryHotelList(state, action) {
      state.invalidSecondaryHotelList = action?.payload;
    },
    getRatePlanData(state) {
      state.loading = false;
    },
    getRatePlanDataSucceeded(state, action) {
      state.loading = false;
      state.ratePlanData = action?.payload;
    },
    getRatePlanDataFailed(state) {
      state.loading = false;
    },
    getConsortiaData(state) {
      state.loading = false;
    },
    getConsortiaSucceeded(state, action) {
      state.loading = false;
      state.consortiaData = action?.payload;
    },
    getConsortiaFailed(state) {
      state.loading = false;
    },
    getPaceData(state) {
      state.loading = false;
    },
    getPaceDataSucceeded(state, action) {
      state.loading = false;
      state.paceData = action?.payload;
    },
    getPaceDataFailed(state) {
      state.loading = false;
    },
    getRoomTypeData(state) {
      state.loading = false;
    },
    getRoomTypeDataSucceeded(state, action) {
      state.loading = false;
      state.roomTypeData = action?.payload;
    },
    getRoomTypeDataFailed(state) {
      state.loading = false;
    },
    getIataData(state) {
      state.loading = true;
    },
    getIataDataSucceeded(state, action) {
      state.loading = false;
      state.iataData = action?.payload;
    },
    getIataDataFailed(state) {
      state.loading = false;
    },
    setIsDashboardLeapSelection(state, action) {
      state.isLeapSelection = action?.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signInActions.signIn, (state) => ({
        ...state,
        ...initialState,
      }))
      .addCase(dashboardActions.getVisualFiltersSucceeded, (state, action) => {
        const dateComparisonFilter = action?.payload?.results?.filter(
          (filter) => filter?.tags?.includes(TAGS.DATE_COMPARISON) && filter?.isDefault === true
        )[0];
        return {
          ...state,
          dateComparison: dateComparisonFilter?.values?.find((filter) => filter.isDefault === true)
            ?.value,
          alignment: {
            id: dateComparisonFilter?.id,
            label: capitalizeFirst(dateComparisonFilter?.type),
          },
          workbookAlignmentToggle: dateComparisonFilter?.type === ALIGNED,
        };
      })
      .addCase(dashboardActions.getHotelListSucceeded, (state, action) => {
        const hotel = getHotelFocusOnValue(
          state.isDemoUser,
          action?.payload,
          state.isDemoModeActivated
        );
        return {
          ...state,
          hotelFocusOn: hotel?.focusOnValues?.[0],
        };
      })
      .addCase(dashboardActions.getVisualsSucceeded, (state, action) => {
        const visualDetails = action?.payload?.results;
        return {
          ...state,
          workbookHotelVisuals: visualDetails?.filter((visual) =>
            visual?.tags?.includes(REPORT_TYPE.WORKBOOK_REPORT_HOTEL)
          ),
          ratePlanVisuals: visualDetails?.filter((visual) =>
            visual?.tags?.includes(REPORT_TYPE.WORKBOOK_REPORT_RATE_PLAN)
          ),
          travelAgentDetailsOffVisuals: visualDetails?.filter((visual) =>
            visual?.tags?.includes(REPORT_TYPE.WORKBOOK_REPORT_IATA_OFF)
          ),
          travelAgentDetailsOnVisuals: visualDetails?.filter((visual) =>
            visual?.tags?.includes(REPORT_TYPE.WORKBOOK_REPORT_IATA_ON)
          ),
        };
      })
      .addCase(dashboardActions.setHotelName, (state, action) => ({
        ...state,
        hotelFocusOn: action?.payload,
        originalFocusOn: action?.payload,
        normalizedView: false,
        enableSecondaryHotelGroups: action?.payload && 'hotels' in action.payload,
      }))
      .addCase(dashboardActions.setSelectedHotelGroup, (state, action) => {
        let secondaryHotelSet;
        if (state && state.hotelFocusOn && 'hotels' in state.hotelFocusOn) {
          if (!isEmpty(state.hotelName) && action?.payload?.hotelGroupDropdownList?.length === 0) {
            secondaryHotelSet = action?.payload?.hotelList[0];
          } else if (state.hotelFocusOn && action?.payload?.hotelGroupDropdownList?.length > 0) {
            const newGroup = action?.payload?.hotelGroupDropdownList?.find(
              (set) => set?.id === state.hotelFocusOn?.id
            );
            if (newGroup) {
              secondaryHotelSet = newGroup;
            } else {
              secondaryHotelSet = action?.payload?.hotelList[0];
            }
          } else {
            secondaryHotelSet = action?.payload?.hotelList[0];
          }
          return {
            ...state,
            hotelFocusOn: secondaryHotelSet,
            enableSecondaryHotelGroups: secondaryHotelSet && 'hotels' in secondaryHotelSet,
          };
        }
        return { ...state };
      })
      .addCase(dashboardActions.setEnableHotelGroups, (state, action) => ({
        ...state,
        enableSecondaryHotelGroups: action?.payload,
        normalizedView: false,
      }));
  },
});
//
export const { actions: workbookActions } = workbookSlice;
